/**
 * Dashboard do Programa GSDIa — Teste de Imunogenicidade (HNS-1736).
 *
 * Entra na Home pelo motor de dashboards do programa: a chave `gsdia_dashboard` em
 * `configuracoes.graficos_dashboard` traz este bloco.
 *
 * Le o endpoint /dashboards/gsdia/, que agrega as mesmas views SQL dos relatorios do
 * programa (gsdia_*) - por isso os numeros batem com os relatorios exportados. Responde
 * as perguntas do descritivo: onde estao as demandas, se as etapas cumprem o prazo,
 * quem demanda (medico e centro), por onde passa a coleta e quanto custa.
 *
 * O financeiro so chega para gestao e administracao: o servidor nem o envia para a
 * operacao, e o bloco some sozinho quando `ve_financeiro` vem falso.
 */
import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Spinner, Alert, Form, Button, Table } from "react-bootstrap";
import { ResponsivePie } from "@nivo/pie";
import { FaFilter, FaEraser } from "react-icons/fa";
import api from "../../../services/api";
import { getCurrentUser, tiposAcesso } from "../../../common/utils";
import { withRouter } from "../../../common/with-router";

//Quem acompanha o programa: operacao (navegadora), gestao interna, a industria e o admin.
//Prestadores nao entram - veem apenas os seus atendimentos. O servidor confere de novo.
const podeVerDashboard = () => {
    const usuario = getCurrentUser();
    if (usuario['is_superuser']) return true;

    const tipo = usuario['tipo_usuario'];
    if (tipo === '' || tipo === 'operador') return true;

    if (tipo === 'gestor') {
        const acessos = tiposAcesso() || {};
        const tipoGestor = acessos.gestor?.tipo_gestor;
        return tipoGestor === 'interno' || tipoGestor === 'industria';
    }

    return false;
};

//abre o atendimento a partir da lista: administração, navegadora e gestão interna (a indústria só vê os números)
const podeAbrirAtendimento = () => {
    const usuario = getCurrentUser();
    if (usuario['is_superuser']) return true;

    const tipo = usuario['tipo_usuario'];
    if (tipo === '' || tipo === 'administrador' || tipo === 'operador') return true;

    if (tipo === 'gestor') {
        const acessos = tiposAcesso() || {};
        return acessos.gestor?.tipo_gestor === 'interno';
    }

    return false;
};

const COR_PRINCIPAL = '#00ADD8';
const COR_APOIO = '#333159';
const COR_POSITIVO = '#1e7e34';
const COR_ALERTA = '#a41c12';
const COR_ATENCAO = '#c78500';
const CINZA = '#6b7a8d';
const TRILHO = '#edf0f4';

const COR_RESULTADO = { Positivo: COR_ATENCAO, Negativo: COR_POSITIVO };

const formataHoras = (horas) => {
    if (horas === null || horas === undefined) return '-';
    const valor = Number(horas);
    return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}h`;
};

const formataDias = (horas) => {
    if (horas === null || horas === undefined) return '';
    return `${(Number(horas) / 24).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`;
};

const formataMoeda = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

//cor do percentual de etapas no prazo: verde a partir de 90%, amarelo a partir de 70%
const corDoPrazo = (pct) => {
    if (pct === null || pct === undefined) return CINZA;
    if (pct >= 90) return COR_POSITIVO;
    if (pct >= 70) return COR_ATENCAO;
    return COR_ALERTA;
};

class GsdiaDashboard extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dados: null,
            carregando: true,
            erro: '',
            data_de: '',
            data_ate: '',
        };
    }

    //a lista traz só o id; a tela do atendimento abre pelo uuid
    abrirAtendimento = async (atendimentoId) => {
        try {
            const { data } = await api.get(`/atendimentos/${atendimentoId}/`);
            if (data?.uuid) {
                this.props.router.navigate(`/atendimentos/edit/${data.uuid}`);
            }
        } catch (error) {
            console.error('Não foi possível abrir o atendimento:', error);
        }
    };

    componentDidMount() {
        this.carregar();
    }

    getProgramaId = () => {
        const usuario = getCurrentUser();
        return this.props.programaId || usuario['ambiente_programa_id'] || null;
    };

    carregar = async () => {
        const programa_id = this.getProgramaId();

        if (!programa_id) {
            this.setState({ carregando: false, erro: 'Selecione um programa para ver os indicadores.' });
            return;
        }

        this.setState({ carregando: true, erro: '' });

        try {
            const params = { programa_id };
            if (this.state.data_de) params.data_de = this.state.data_de;
            if (this.state.data_ate) params.data_ate = this.state.data_ate;

            const { data } = await api.get('/dashboards/gsdia/', { params });
            this.setState({ dados: data, carregando: false });
        } catch (e) {
            console.error('Erro ao carregar o dashboard do GSDIa:', e);
            this.setState({
                carregando: false,
                erro: e?.response?.data?.error || 'Não foi possível carregar os indicadores.',
            });
        }
    };

    limpar = () => this.setState({ data_de: '', data_ate: '' }, this.carregar);

    renderIndicador = (titulo, valor, complemento, cor = COR_APOIO) => (
        <Col xs={6} md={4} xl={2} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="py-3">
                    <div style={{ fontSize: '11px', color: CINZA, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                        {titulo}
                    </div>
                    <div style={{
                        fontSize: String(valor).length > 9 ? '19px' : '26px',
                        fontWeight: 700, color: cor, lineHeight: 1.25, whiteSpace: 'nowrap'
                    }}>{valor}</div>
                    {complemento && <div style={{ fontSize: '11px', color: CINZA }}>{complemento}</div>}
                </Card.Body>
            </Card>
        </Col>
    );

    renderCard = (titulo, subtitulo, conteudo, altura = 320, largura = 6) => (
        <Col lg={largura} className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pb-0">
                    <div className="fw-bold">{titulo}</div>
                    {subtitulo && <small className="text-muted">{subtitulo}</small>}
                </Card.Header>
                <Card.Body style={altura ? { height: `${altura}px` } : undefined}>{conteudo}</Card.Body>
            </Card>
        </Col>
    );

    renderTabela = (titulo, subtitulo, conteudo, largura = 6) => (
        <Col lg={largura} className="mb-4">
            <Card className="h-100 border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pb-0">
                    <div className="fw-bold">{titulo}</div>
                    {subtitulo && <small className="text-muted">{subtitulo}</small>}
                </Card.Header>
                {/* rolagem no proprio card: o .table-responsive do index.css cola a tabela nas bordas */}
                <Card.Body style={{ maxHeight: '340px', overflow: 'auto' }}>{conteudo}</Card.Body>
            </Card>
        </Col>
    );

    //barras em HTML com o nome da etapa em cima: o texto inteiro cabe, sem a margem fixa do eixo
    renderBarra = (partes) => (
        <div className="d-flex" style={{ background: TRILHO, borderRadius: 4, height: 8, overflow: 'hidden' }}>
            {partes.filter(p => p.pct > 0).map((p) => (
                <div key={p.cor} style={{ width: `${p.pct}%`, background: p.cor }} />
            ))}
        </div>
    );

    renderDemandasPorEtapa = (porEtapa) => {
        const maior = Math.max(...porEtapa.map(e => e.atendimentos), 1);
        return (
            <div className="d-flex flex-column gap-2">
                {porEtapa.map((e) => {
                    const cor = e.atendimentos ? COR_APOIO : CINZA;
                    return (
                        <div key={e.ordem}>
                            <div className="d-flex justify-content-between mb-1" style={{ fontSize: '12px', color: cor }}>
                                <span>{e.ordem}. {e.etapa}</span>
                                <span className="fw-bold ms-3">{e.atendimentos}</span>
                            </div>
                            {this.renderBarra([{ cor: COR_PRINCIPAL, pct: (e.atendimentos / maior) * 100 }])}
                        </div>
                    );
                })}
            </div>
        );
    };

    renderPrazoPorEtapa = (slaPorEtapa) => (
        <div className="d-flex flex-column gap-2">
            {slaPorEtapa.filter(e => e.prazo_horas).map((e) => {
                const total = e.no_prazo + e.fora_prazo;
                const pct = total ? Math.round((e.no_prazo / total) * 100) : null;
                return (
                    <div key={e.ordem}>
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '12px', color: total ? COR_APOIO : CINZA }}>
                            <span>
                                {e.ordem}. {e.etapa}
                                <span style={{ color: CINZA }}> · prazo {formataHoras(e.prazo_horas)}</span>
                            </span>
                            <span className="fw-bold ms-3 text-nowrap" style={{ color: total ? corDoPrazo(pct) : CINZA }}>
                                {total ? `${pct}% · ${e.no_prazo} de ${total}` : 'sem passagens'}
                            </span>
                        </div>
                        {this.renderBarra(total ? [
                            { cor: COR_POSITIVO, pct: (e.no_prazo / total) * 100 },
                            { cor: COR_ALERTA, pct: (e.fora_prazo / total) * 100 },
                        ] : [])}
                    </div>
                );
            })}
            <div className="d-flex gap-3 mt-2" style={{ fontSize: '11px', color: CINZA }}>
                <span><span style={{ color: COR_POSITIVO }}>●</span> No prazo</span>
                <span><span style={{ color: COR_ALERTA }}>●</span> Fora do prazo</span>
            </div>
        </div>
    );

    semDados = (mensagem = 'Sem registros no período') => (
        <div className="d-flex align-items-center justify-content-center h-100 py-4">
            <span className="text-muted" style={{ fontSize: '13px' }}>{mensagem}</span>
        </div>
    );

    render() {
        const { dados, carregando, erro, data_de, data_ate } = this.state;

        if (!podeVerDashboard()) return null;
        if (carregando) return <div className="text-center py-5"><Spinner animation="border" /></div>;
        if (erro) return <Alert variant="warning" className="m-4">{erro}</Alert>;

        const resumo = dados?.resumo || {};
        const porEtapa = dados?.por_etapa || [];
        const slaPorEtapa = dados?.sla_por_etapa || [];
        const atrasadas = dados?.atrasadas || [];
        const resultados = (dados?.resultados || []).map(r => ({ ...r, color: COR_RESULTADO[r.id] || CINZA }));
        const medicos = dados?.demanda_por_medico || [];
        const centros = dados?.demanda_por_centro || [];
        const laboratorios = dados?.volume_por_laboratorio || [];
        const financeiro = dados?.financeiro;

        const temDemanda = (resumo.demandas ?? 0) > 0;
        const temAbertas = porEtapa.some(e => e.atendimentos > 0);
        const temSla = slaPorEtapa.some(e => e.no_prazo + e.fora_prazo > 0);

        return (
            <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                        <h5 className="fw-bold m-0">Programa GSDIa — Teste de Imunogenicidade</h5>
                        <small className="text-muted">
                            Mesma base dos relatórios: demandas, prazos das etapas, resultados e rede de provedores
                        </small>
                    </div>
                </div>

                {/* filtros */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-3">
                        <Row className="align-items-end g-2">
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Demandas cadastradas de</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_de}
                                    onChange={(e) => this.setState({ data_de: e.target.value })}
                                />
                            </Col>
                            <Col sm={6} lg={3}>
                                <Form.Label style={{ fontSize: '12px', color: CINZA }}>Até</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={data_ate}
                                    onChange={(e) => this.setState({ data_ate: e.target.value })}
                                />
                            </Col>
                            <Col sm={12} lg={6} className="d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={this.carregar}>
                                    <FaFilter className="me-1" /> Filtrar
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={this.limpar}>
                                    <FaEraser className="me-1" /> Limpar
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* indicadores */}
                <Row>
                    {this.renderIndicador('Demandas', resumo.demandas ?? 0, 'cadastradas no período')}
                    {this.renderIndicador('Em andamento', resumo.em_andamento ?? 0, 'no fluxo da amostra', COR_PRINCIPAL)}
                    {this.renderIndicador(
                        'Laudos liberados',
                        resumo.laudos_liberados ?? 0,
                        resumo.media_horas_ate_laudo !== null && resumo.media_horas_ate_laudo !== undefined
                            ? `média de ${formataDias(resumo.media_horas_ate_laudo)} (meta ${formataDias(resumo.sla_total_horas)})`
                            : `meta de ${formataDias(resumo.sla_total_horas)}`,
                        COR_POSITIVO
                    )}
                    {this.renderIndicador(
                        'Recoletas',
                        resumo.recoletas ?? 0,
                        `em ${resumo.atendimentos_com_recoleta ?? 0} atendimento(s)`,
                        COR_ATENCAO
                    )}
                    {this.renderIndicador(
                        'Etapas no prazo',
                        resumo.etapas_no_prazo_pct === null || resumo.etapas_no_prazo_pct === undefined
                            ? '-'
                            : `${Number(resumo.etapas_no_prazo_pct).toLocaleString('pt-BR')}%`,
                        `de ${resumo.etapas_avaliadas ?? 0} etapa(s) com prazo`,
                        corDoPrazo(resumo.etapas_no_prazo_pct)
                    )}
                    {this.renderIndicador(
                        'Atrasadas agora',
                        resumo.atrasadas_agora ?? 0,
                        'etapas abertas fora do prazo',
                        (resumo.atrasadas_agora ?? 0) > 0 ? COR_ALERTA : COR_POSITIVO
                    )}
                </Row>

                <Row>
                    {/* funil: onde estao as demandas abertas */}
                    {this.renderCard(
                        'Onde estão as demandas',
                        'Demandas em andamento pela etapa atual do fluxo',
                        temAbertas
                            ? this.renderDemandasPorEtapa(porEtapa)
                            : this.semDados(temDemanda ? 'Nenhuma demanda em andamento' : 'Sem demandas no período'),
                        null, 6
                    )}

                    {/* SLA por etapa */}
                    {this.renderCard(
                        'Cumprimento do prazo por etapa',
                        'Passagens pelas etapas dentro e fora do prazo de acompanhamento',
                        temSla
                            ? this.renderPrazoPorEtapa(slaPorEtapa)
                            : this.semDados('Nenhuma etapa com prazo concluída ou em andamento'),
                        null, 6
                    )}
                </Row>

                <Row>
                    {/* etapas atrasadas */}
                    {this.renderTabela(
                        'Etapas atrasadas agora',
                        'Etapas abertas que já passaram do prazo — das mais atrasadas para as menos',
                        atrasadas.length ? (
                            <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>Atendimento</th>
                                        <th>Etapa</th>
                                        <th className="text-center">Na etapa</th>
                                        <th className="text-center">Prazo</th>
                                        <th className="text-center">Atraso</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {atrasadas.map((a) => (
                                        <tr key={`${a.atendimento_id}-${a.etapa_ordem}`}>
                                            <td>
                                                {podeAbrirAtendimento() ? (
                                                    <Button variant="link" size="sm" className="p-0 align-baseline" style={{ fontSize: '13px' }}
                                                        title="Abrir atendimento" onClick={() => this.abrirAtendimento(a.atendimento_id)}>
                                                        #{a.atendimento_id}
                                                    </Button>
                                                ) : `#${a.atendimento_id}`}
                                            </td>
                                            <td>{a.etapa_ordem}. {a.etapa}</td>
                                            <td className="text-center">{formataHoras(a.horas_na_etapa)}</td>
                                            <td className="text-center">{formataHoras(a.prazo_horas)}</td>
                                            <td className="text-center fw-bold" style={{ color: COR_ALERTA }}>
                                                {formataHoras(a.horas_de_atraso)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : this.semDados('Nenhuma etapa atrasada'),
                        7
                    )}

                    {/* resultados */}
                    {this.renderCard(
                        'Resultado dos testes',
                        'Desfecho informado pela ARUP nos laudos liberados',
                        resultados.length ? (
                            <ResponsivePie
                                data={resultados}
                                margin={{ top: 16, right: 16, bottom: 56, left: 16 }}
                                innerRadius={0.6}
                                padAngle={0.8}
                                cornerRadius={3}
                                colors={{ datum: 'data.color' }}
                                enableArcLinkLabels={false}
                                arcLabelsTextColor="#fff"
                                legends={[{
                                    anchor: 'bottom', direction: 'row', translateY: 44,
                                    itemWidth: 100, itemHeight: 18, symbolSize: 11, symbolShape: 'circle'
                                }]}
                            />
                        ) : this.semDados('Nenhum laudo liberado no período'),
                        300, 5
                    )}
                </Row>

                <Row>
                    {/* demanda por medico */}
                    {this.renderTabela(
                        'Demanda por médico',
                        'Médicos que mais solicitaram o teste no período',
                        medicos.length ? (
                            <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>Médico</th>
                                        <th>CRM</th>
                                        <th className="text-center">Demandas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {medicos.map((m, i) => (
                                        <tr key={`${m.medico}-${i}`}>
                                            <td>{m.medico}</td>
                                            <td>{m.crm || '-'}</td>
                                            <td className="text-center">{m.demandas}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : this.semDados(),
                        6
                    )}

                    {/* demanda por centro */}
                    {this.renderTabela(
                        'Demanda por centro de referência',
                        'Pelo centro em que o médico atende — médico em mais de um centro conta em cada um',
                        centros.length ? (
                            <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>Centro</th>
                                        <th>Cidade/UF</th>
                                        <th className="text-center">Demandas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {centros.map((c, i) => (
                                        <tr key={`${c.centro}-${i}`}>
                                            <td>{c.centro}</td>
                                            <td>{c.local || '-'}</td>
                                            <td className="text-center">{c.demandas}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : this.semDados(),
                        6
                    )}
                </Row>

                <Row>
                    {/* volume por laboratorio de coleta */}
                    {this.renderTabela(
                        'Volume por laboratório de coleta',
                        'Coletas agendadas e realizadas por laboratório',
                        laboratorios.length ? (
                            <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>Laboratório</th>
                                        <th>Cidade/UF</th>
                                        <th className="text-center">Agendadas</th>
                                        <th className="text-center">Realizadas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {laboratorios.map((l, i) => (
                                        <tr key={`${l.laboratorio}-${i}`}>
                                            <td>{l.laboratorio}</td>
                                            <td>{l.local || '-'}</td>
                                            <td className="text-center">{l.agendadas}</td>
                                            <td className="text-center">{l.realizadas}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : this.semDados('Nenhuma coleta agendada no período'),
                        financeiro ? 5 : 12
                    )}

                    {/* financeiro: so para gestao e administracao */}
                    {financeiro && this.renderTabela(
                        'Volume financeiro',
                        'Valor agendado e realizado por tipo de serviço — o exame da ARUP é faturado pela Ultragenyx',
                        <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    <th>Serviço</th>
                                    <th className="text-center">Agendados</th>
                                    <th className="text-center">Realizados</th>
                                    <th className="text-end">Valor agendado</th>
                                    <th className="text-end">Valor realizado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {financeiro.por_tipo.map((f) => (
                                    <tr key={f.tipo}>
                                        <td>{f.tipo}</td>
                                        <td className="text-center">{f.quantidade}</td>
                                        <td className="text-center">{f.realizados}</td>
                                        <td className="text-end">{formataMoeda(f.valor_agendado)}</td>
                                        <td className="text-end">{formataMoeda(f.valor_realizado)}</td>
                                    </tr>
                                ))}
                                <tr className="fw-bold">
                                    <td colSpan={3}>Total</td>
                                    <td className="text-end">{formataMoeda(financeiro.total_agendado)}</td>
                                    <td className="text-end">{formataMoeda(financeiro.total_realizado)}</td>
                                </tr>
                            </tbody>
                        </Table>,
                        7
                    )}
                </Row>
            </div>
        );
    }
}

GsdiaDashboard.propTypes = {
    programaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    router: PropTypes.object,
};

export default withRouter(GsdiaDashboard);
